import { $api } from '../y/api';
import { YI18NString } from '../y/i18n';
import { $time } from '../y/time';
import { YCountryId, YId, YTimeString } from '../y/types';
import { CBookingId, CBookingPartId, CBookingPartStatus, CRating } from './c-booking';
import { c_cities, CCityId } from './c-city';
import { CEventId } from './c-event';
import { CSessionId } from './c-event-admin';
import { CTicketType } from './c-producer';
import { c_venues, CVenueSceneView } from './c-venue';
import { EMaybePaymentIntent } from './e-payment-intent';
import { TTicketHomeView } from './t-ticket';
import { legacy_events, LegacyAvailability, LegacyEventModel } from './zzz-legacy-open-events';

export interface CHBookingListSession {
    id: CSessionId;
    event_id: CEventId;
    is_drop_in: boolean;
    time: YTimeString;
    end_time?: YTimeString;
    sales_end: number;
    duration: number;
}

export interface CHBookingList {
    id: CBookingId;
    part_id: CBookingPartId;
    quantity: number;
    status: CBookingPartStatus;
    session: CHBookingListSession;
    event_name: YI18NString;
    city_id: CCityId;
    event_slug: string;
    venue_name: string;
    expires_at?: YTimeString;
    inactive_at: YTimeString;
    sales_end_at: YTimeString;
    rating?: CRating;
    picture_id: YId;
}

export interface CBookingHomeView {
    id: CBookingId;
    part_id: CBookingPartId;
    is_test: boolean;
    session_id: YId;
    session_time: YTimeString;
    sales_end_at: YTimeString;
    event_id: YId;
    token: string;
    number: string;
    quantity: number;
    base_quantity: number;
    status: CBookingPartStatus;
    rating?: CRating;
    expires_at?: YTimeString;
    has_purchases: boolean;
}

export function cBookingsBook(session_id: YId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CBookingHomeView>>('c/bookings/book', { session_id, quantity });
}

export function cBookingsStandby(session_id: YId, quantity: number, booking_id?: CBookingId) {
    return $api.POST<CBookingHomeView>('c/bookings/standby', { session_id, quantity, booking_id });
}

export function cBookingsNewBook(session_id: YId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CHBookingList>>('c/bookings/new_book', { session_id, quantity });
}

export function cBookingsNewStandby(session_id: YId, quantity: number, booking_id?: CBookingId) {
    return $api.POST<CHBookingList>('c/bookings/new_standby', { session_id, quantity, booking_id });
}

export function cBookingBuyMore(booking_id: CBookingId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CBookingHomeView>>(`c/booking/${booking_id}/buy_more`, { quantity });
}

export function cBookingNewBuyMore(booking_id: CBookingId, quantity: number) {
    return $api.POST<EMaybePaymentIntent<CHBookingList>>(`c/booking/${booking_id}/buy_more`, { quantity });
}

interface BookingInfoResponse {
    booking: CBookingHomeView;
    availability: LegacyAvailability;
    event: LegacyEventModel;
    venue: CVenueSceneView;
    ticket_type: CTicketType;
    tickets?: TTicketHomeView[];
}

export function cBookingPartInfoView(id: CBookingPartId) {
    return $api.GET<BookingInfoResponse>(`c/booking_part/${id}/info_view`).then(r => {
        c_venues.register(r.venue);
        return [r.booking, legacy_events.register(r.event), r.venue, r.availability, r.ticket_type, r.tickets || []] as const;
    });
}

interface UnbookInfoResponse {
    is_late: boolean;
}

export function cBookingPartGetUnbookInfo(id: CBookingPartId) {
    return $api.GET<UnbookInfoResponse>(`c/booking_part/${id}/unbook`)
}

export function chBookingList(reload?: boolean) {
    return $api.cachedGET<CHBookingList[]>(`c/bookings/home_list_view`, reload);
}

export function chBookingEventLocation(b: CHBookingList) {
    return ['event', c_cities.maybe(b.city_id)!.slug, b.event_slug];
}

export function chBookingEventUrl(b: CHBookingList, country: YCountryId) {
    if(country == 'DE')
        return 'https://abundolive.de/' + chBookingEventLocation(b).join('/');
    else
        return 'https://abundolive.se/' + chBookingEventLocation(b).join('/');
}

export function chBookingListFilterStandby(b: CHBookingList) {
    return (b.status == 'standby' || b.status == 'standby_reserved') && !$time(b.sales_end_at).is_past;
}

export function chBookingListSortStandby(a: CHBookingList, b: CHBookingList) {
    if(a.status == 'standby_reserved') {
        if(b.status != 'standby_reserved')
            return -1;
    } else if(b.status == 'standby_reserved') {
        return 1;
    }

    return $time.compare($time(a.session.time), $time(b.session.time));
}

export function chBookingListFilterActive(b: CHBookingList) {
    return b.status == 'confirmed' && $time(b.inactive_at).is_future;
}

export function chBookingListSortActive(a: CHBookingList, b: CHBookingList) {
    return $time.compare($time(a.session.time), $time(b.session.time));
}

export function chBookingListFilterPast(b: CHBookingList) {
    return b.status == 'confirmed' && $time(b.inactive_at).is_past;
}

export function chBookingListSortPast(a: CHBookingList, b: CHBookingList) {
    return $time.compare($time(b.session.time), $time(a.session.time));
}
